/*
 * Authors: mariushirjanu
 * Created: 21/12/2022
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { util } from '@app/shared/util';
import { HttpDataService, IHttpDataCache } from '@app/shared/http/http-data.service';
import { OrderCatalogService } from './order-catalog.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IDescribedObject, INumericObject, IObject, IPrice, IProductImage } from '@type_definitions/datatypes';
import { ICatalogGuideCodePart } from '../../webshop/guide/webshop-guide-data.service';
import { IPropertyGroup } from '../../globalproduct/global-product-data.service';

export interface ICatalogCategory {
  name:string;
  type:'INTERNAL' | 'EXTERNAL';
  accessType:'CATEGORY' | 'BUTTON';
  punchoutProtocol:string;
  postUrl:string;
  message:string;
  description?:string;
}

export interface ICatalogProductBundle extends ICatalogProduct {
  quantity:number;
  _price:Function;
}

export interface ICatalogProduct extends IDescribedObject {
  projectId:number;
  countryId:number;
  country:string;
  category:string;
  category_sequence:number;
  part:string;
  globalPart:string;
  partConfigurationExist:boolean;
  partConfigurationId:number;
  partConfigurationCategory:string;
  title:string;
  shortDescription:string;
  vendorId:number;
  vendor:string;
  orderable:boolean;
  warning:boolean;
  url:string;
  show_catalog_in_order:boolean;
  show_catalog_in_quote:boolean;
  show_catalog_in_punchout:boolean;
  price:IPrice;
  unitPrice?:number;
  favourite?:boolean;
  EOL:{
    date:string;
    warning:boolean
  };
  compatibleProducts:boolean;
  isMachine:boolean;
  leadTime:{
    days:number;
    warning:boolean;
    text:string;
  }
  documentUrl?:string;
  documentId?:number;
  documentDescription?:string;
  images?:IProductImage[];
  bundle?:ICatalogProductBundle[];
  showIcecatDetails?:boolean;
  optional?:boolean;
  stock?:number;
  endOfLifeDate?:Date;
  validityDate?:Date;
  properties?:IPropertyGroup[];
  initialLeasePrice?:number;
  initialLeasePriceTotal?:number;
  unitLeasePrice?:IPrice;
  unitLeasePriceTotal?:IPrice;
  leasePeriod?:number;
  image?:IProductImage;
}

export interface IProductSearchList {
  categories:INumericObject;
  pages:{
    current:number;
    total:number;
  };
  products:ICatalogProduct[]|ICatalogGuideCodePart[];
  filters:IObject;
}

@Injectable()
export class OrderCatalogDataService {
  private filtersCache:{};
  private cache:IHttpDataCache<any>;

  constructor(
    private orderCatalogService:OrderCatalogService,
    private httpDataService:HttpDataService,
    private formattingService:FormattingService
  ) {
    this.filtersCache = {};
    this.cache = httpDataService.createCache();
  }

  public start():void {
    this.filtersCache = {};
  }

  public filters(category:string):Promise<IObject[]> {
    if (this.filtersCache[ category ]) {
      return Promise.resolve(this.filtersCache[ category ]);
    }
    return this.httpDataService.doGET(`${this.urlPrefix}filters`, { category: category }, undefined, 'Error loading filters')
      .then((responseData:IObject[]) => {
        this.filtersCache[ category ] = responseData;
        return responseData;
      });
  }

  public deleteFavourite = (productId:number):Promise<any> => {
    return this.httpDataService.doDELETE(
      `${this.urlPrefix}favourite/${productId}`,
      undefined,
      undefined,
      'Error deleting favourite'
    );
  };

  public saveFavourite = (productId:number):Promise<any> => {
    return this.httpDataService.doPOST(
      `${this.urlPrefix}favourite/${productId}`,
      undefined,
      undefined,
      undefined,
      'Error saving favourite'
    );
  };

  public toggleFavourite = (productId:number, isFavourite:boolean):Promise<any> => {
    if (isFavourite) {
      return this.saveFavourite(productId);
    } else {
      return this.deleteFavourite(productId);
    }
  };

  private currentCurrency():string {
    let currency = this.orderCatalogService.currency;
    if (currency && currency !== 'USD' && currency !== 'EUR') {
      currency = 'LOC';
    }
    return currency;
  }

  public searchAll():Promise<IProductSearchList> {
    const params = {
      num: 0,
      bpCompanyCountryId: this.orderCatalogService.companyCountryId
    };

    return this.httpDataService.doGET(`${this.urlPrefix}products`, params, 'Loading products', 'Loading products failed');
  }

  public search(productId:number, terms?, favourites?, category?:string, buyingGuideCode?:string, filters?:any, num?:number, page?:number, pmin?:number, pmax?:number, stock?, sort?, stockOrderType?: string):Promise<IProductSearchList> {
    if (!stock) {
      stock = undefined;
    }

    if (category?.toLowerCase() === 'all') {
      category = undefined;
    }

    let params;

    if (productId) {
      params = {
        bpCompanyCountryId: this.orderCatalogService.companyCountryId,
        currency: this.currentCurrency(),
        billType: this.orderCatalogService.billType,
        workflowType: this.orderCatalogService.workflowType,
        compatibleWith: productId,
        terms,
        category,
        num,
        page,
        stockOrderType
      };
    } else {
      params = {
        bpCompanyCountryId: this.orderCatalogService.companyCountryId,
        terms,
        category,
        buyingGuideCode,
        num,
        page,
        pmin,
        pmax,
        sort,
        stock,
        currency: this.currentCurrency(),
        billType: this.orderCatalogService.billType,
        workflowType: this.orderCatalogService.workflowType,
        stockOrderType
      };

      if(favourites){
        params.favourites = favourites;
      }

      _.forEach(filters, (value:any, key) => {
        if (value && value.length) {
          params[`filter.${key}`] = value;
        }
      });
    }

    return this.cache.doGET(`${this.urlPrefix}products`, params, undefined, 'Search failed')
      .then((responseData:IProductSearchList) => {
        this.formatProducts(responseData);
        return responseData;
      })
      .catch(util.noop);
  }

  public product(productId:number, partNumber:string, bundleId:number, summary:boolean, showError:boolean = true):Promise<ICatalogProduct> {
    const params = {
      currency: this.currentCurrency(),
      billType: this.orderCatalogService.billType,
      bundle: bundleId,
      productId,
      partNumber: partNumber? encodeURIComponent(partNumber) : undefined,
      summary
    };
    return this.httpDataService.doGET(`${this.urlPrefix}part`, params, undefined, showError? 'Error loading product details' : undefined)
      .then((product:ICatalogProduct) => {
        this.formatLeadTime(product);
        return product;
      });
  }

  public formatProducts(responseData:IProductSearchList):void {
    _.forEach((<ICatalogProduct[]>responseData.products), (product:ICatalogProduct) => {
      product.unitPrice = product.price.amount;
      product.initialLeasePrice = product.unitLeasePrice?.amount;
      product.initialLeasePriceTotal = product.unitLeasePriceTotal?.amount;
      this.formatLeadTime(product);
    });
  }

  private formatLeadTime(product:ICatalogProduct) {
    if (product.leadTime) {
      product.leadTime.text = this.formattingService.formatBusinessDays(product.leadTime.days);
    }
  }

  private get urlPrefix():string {
    return `rest/catalog/${this.orderCatalogService.webshopId}/${this.orderCatalogService.countryId}/`;
  }

  public categories(workflowType:string, stockOrderType?:string):Promise<ICatalogCategory[]> {
    this.orderCatalogService.startLoadingCategories();
    return this.cache.doGET(`${this.urlPrefix}categories`, {
      bpCompanyCountryId: this.orderCatalogService.companyCountryId,
      stockOrderType,
      workflowType
    },
      undefined,
      'Error loading categories'
    )
      .then((categories:ICatalogCategory[]) => {
        this.orderCatalogService.categories = categories;
        return categories;
      })
      .catch(util.noop);
  }

  // clear the cache
  public clear():void {
    this.cache.clear();
    this.filtersCache = {};
  }
}